import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeUserInput } from "@/lib/sanitize";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    nome?: string,
    genero?: string,
    telefone?: string,
    localizacao?: string,
    faixaEtaria?: string
  ) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  isAuthenticated: boolean;
  loginBlocked: boolean;
  loginBlockCountdown: number;
}

const AuthContext = createContext<AuthContextType | null>(null);

const MAX_LOGIN_ATTEMPTS = 5;
const BLOCK_DURATION_SECONDS = 30;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginBlocked, setLoginBlocked] = useState(false);
  const [loginBlockCountdown, setLoginBlockCountdown] = useState(0);
  const failedAttemptsRef = useRef(0);
  const blockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const initialSessionResolved = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id ?? null;
      // Only re-render consumers when the user identity actually changes.
      // TOKEN_REFRESHED (fired on tab focus) keeps the same id — skip it
      // to avoid cascading refetches/skeleton flicker across the app.
      if (newUserId !== currentUserIdRef.current) {
        // Clear per-user client-side caches when the identity changes
        // (logout or switching accounts on the same tab).
        try {
          sessionStorage.removeItem("provador-state");
        } catch {}
        currentUserIdRef.current = newUserId;
        setSession(session);
        setUser(session?.user ?? null);
      }
      if (initialSessionResolved.current) {
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      currentUserIdRef.current = session?.user?.id ?? null;
      setSession(session);
      setUser(session?.user ?? null);
      initialSessionResolved.current = true;
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (blockTimerRef.current) clearInterval(blockTimerRef.current);
    };
  }, []);

  const startBlockTimer = () => {
    setLoginBlocked(true);
    setLoginBlockCountdown(BLOCK_DURATION_SECONDS);
    if (blockTimerRef.current) clearInterval(blockTimerRef.current);
    blockTimerRef.current = setInterval(() => {
      setLoginBlockCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(blockTimerRef.current!);
          blockTimerRef.current = null;
          setLoginBlocked(false);
          failedAttemptsRef.current = 0;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const signUp = async (
    email: string,
    password: string,
    nome?: string,
    genero?: string,
    telefone?: string,
    localizacao?: string,
    faixaEtaria?: string
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    const sanitizedNome = nome ? sanitizeUserInput(nome) : undefined;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { nome: sanitizedNome, genero, telefone, localizacao, faixa_etaria: faixaEtaria },
      },
    });

    if (!error && data.user) {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          nome: sanitizedNome,
          genero,
          telefone,
          localizacao,
          faixa_etaria: faixaEtaria,
          email,
        })
        .eq("id", data.user.id);

      if (updateError) {
        console.error("Profile update after signup failed:", updateError.message);
      }

      // Log account creation for admin dashboard
      await supabase.from("admin_audit_logs").insert({
        user_id: data.user.id,
        action_type: "account_creation",
        status: "success",
        metadata: { email, nome: sanitizedNome, genero, localizacao, faixa_etaria: faixaEtaria },
      }).then(({ error: logError }) => {
        if (logError) console.error("Audit log insert failed:", logError.message);
      });
    }

    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    if (loginBlocked) {
      return {
        data: null as any,
        error: {
          message: `Muitas tentativas. Aguarde ${loginBlockCountdown}s.`,
          name: "rate_limited",
          status: 429,
        } as any,
      };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error && data.user) {
      // Reset failed attempts on success
      failedAttemptsRef.current = 0;

      const { data: profile } = await supabase
        .from("profiles")
        .select("blocked")
        .eq("id", data.user.id)
        .maybeSingle();

      if (profile?.blocked) {
        await supabase.auth.signOut();
        return {
          data: null as any,
          error: {
            message: "Sua conta foi bloqueada. Entre em contato com o suporte.",
            name: "blocked",
            status: 403,
          } as any,
        };
      }
    } else if (error) {
      failedAttemptsRef.current += 1;
      if (failedAttemptsRef.current >= MAX_LOGIN_ATTEMPTS) {
        startBlockTimer();
      }

      // Log failed login via edge function (no auth needed)
      supabase.functions.invoke("log-failed-login", {
        body: { email, error_message: error.message },
      }).catch(() => {});
    }

    return { data, error };
  };

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.functions.invoke("send-password-reset", {
      body: {
        email,
        redirectTo: "https://12personas.com.br/reset-password",
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        isAuthenticated: !!user,
        loginBlocked,
        loginBlockCountdown,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
